import customRegex from '@dogfy/lib/regex'
import breedWeights from '../../services/data/breedWeights.json'
import config from '../config'

import { jwtDecode } from 'jwt-decode'
import { customAlphabet } from 'nanoid'

import provincesFR from './provinces-france.json'
import provincesIT from './provinces-italy.json'
import provincesES from './provinces-spain.json'

export function areArraysOverlaped (arr1, arr2) {
  return arr1.some(item => arr2.includes(item))
}

export function cloneObject (obj) {
  return JSON.parse(JSON.stringify(obj))
}

export function scrollIntoElementAndFocus (element) {
  window.addEventListener('scrollend', () => element.focus(), { once: true })
  element.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

export function fmtAmount (amount = 0) {
  if (amount < 10_000) {
    return `${amount.toLocaleString('pt', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
  } else if (amount < 1_000_000) {
    // K formatting
    return `${(amount / 1_000).toLocaleString('pt', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}K €`
  } else {
    // M formatting
    return `${(amount / 1_000_000).toLocaleString('pt', { maximumFractionDigits: 2 })}M €`
  }
}

export function fmtDecimals (number = 0) {
  return number.toLocaleString('pt', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

export function fmtList (list = [], locale = 'es', type = 'conjunction') {
  const formatter = new Intl.ListFormat(locale, { style: 'long', type })
  return formatter.format(list)
}

export function roundToDecimals (number, decimals = 2) {
  const factor = Math.pow(10, decimals)
  return Math.round((number + Number.EPSILON) * factor) / factor
}

export function toCapitalCase (str) {
  return str?.replace(/^(.)/, (match) => match.toUpperCase())
}

export function toTitleCase (str) {
  return str?.toLowerCase().replace(/(?:^|\s|["'/([{-])+\S/g, match => match.toUpperCase())
}

export function toCamelCase (str) {
  return str?.replace(/(?:^\w|[A-Z]|\b\w)/g, (letter, index) => index === 0 ? letter.toLowerCase() : letter.toUpperCase()).replace(/\s+/g, '')
}

export function toPascalCase (str) {
  return str
    ?.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    ?.map((x) => x.charAt(0).toUpperCase() + x.slice(1).toLowerCase())
    ?.join('') || ''
}

export function toCamelFromSnake (snakeCaseString) {
  return snakeCaseString?.toLowerCase().replace(/_([a-z])/g, (match, letter) => letter.toUpperCase())
}

export function toKebabCase (str) {
  return str?.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/[\s+\-+_]/g, '-').toLowerCase()
}

export function toSnakeCase (str) {
  return str?.replace(/([a-z])([A-Z])/g, '$1_$2').replace(/[\s+\-+]/g, '_').replace(/[^\w_]/g, '').toLowerCase()
}

export function normalizeString (str, replacement = '') {
  return str?.trim().toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f-&/\\#,+()$~%.'":*?¿<>’{}”“«»]/g, replacement).replace(/œ/g, 'oe')
}

export function slugify (string) {
  return normalizeString(string)?.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/[\s_]+/g, '-').replace(/-$/g, '')
}

export function toKebabFromCamel (str) {
  return str?.replace(/[A-Z]+(?![a-z])|[A-Z]/g, ($, ofs) => (ofs ? '-' : '') + $.toLowerCase())
}

export function toCamelFromKebab (str) {
  return str
    ?.split('-')
    ?.map((word, index) => index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1))
    ?.join('')
}

export function getURLEncodedObjectParams (object) {
  return `?${Object.keys(object || {})
    .filter(key => !!object[key])
    .map(key => `${key}=${encodeURIComponent(object[key])}`)
    .join('&')}`
}

export function provinceFromCode (code, country) {
  switch (country.toLowerCase()) {
    case 'es': return provincesES[code.slice(0, 2)]
    case 'fr': return provincesFR[code.slice(0, 2)]
    case 'it': return provincesIT[code.slice(0, 3)] || provincesIT[code.slice(0, 2)]
  }
}

export function generateUniqueId () {
  const newId = customAlphabet(config.nanoid.alphabet, config.nanoid.length)
  return newId()
}

export function getBreedWeight (breedCode) {
  if (typeof breedCode !== 'string') breedCode = breedCode.toString()
  if (breedCode.length < 3) breedCode = breedCode.padStart(3, '0')

  const idealWeight = breedWeights[breedCode]

  return idealWeight
}

export function getDogAgeInfo ({ breedCode, age, weight, adultIdealWeight }) {
  // `age` in months, `weight` and `adultIdealWeight` in kg

  /*
  Criteria used to determine if dog is adult
  ------------------------------------------
  Range size       Puppy      Adult
  Under 7kgs       0-11 m     12+ m
  7 - 15kgs        0-13 m     14+ m
  15 – 27.5 kg     0-14 m     15+ m
  27.5 – 47.5 kg   0-16 m     17+ m
  +47.5 kg         0-20 m     21+ m

  Criteria used to determine if dog is senior
  -----------------------------------------------
  More than 10 years old Yes
  Between 7 and 10 years old but Weight > 15kgs Yes
  Between 7 and 10 years old but Weight <= 15kgs No
  Less than 10 years old No

  (This info extracted from the received documentation).
  */

  if (typeof breedCode !== 'string') breedCode = breedCode.toString()
  if (breedCode.length < 3) breedCode = breedCode.padStart(3, '0')

  let isPuppy = null
  let isAdult = null
  let isSenior = null

  const idealWeight = adultIdealWeight || breedWeights[breedCode]

  if (!idealWeight) {
    return { idealWeight, isPuppy, isAdult, isSenior }
  }

  isPuppy = false
  if (idealWeight <= 7) {
    if (age < 12) {
      isPuppy = true
    }
  } else if (idealWeight <= 15) {
    if (age < 14) {
      isPuppy = true
    }
  } else if (idealWeight <= 27.5) {
    if (age < 15) {
      isPuppy = true
    }
  } else if (idealWeight <= 47.5) {
    if (age < 17) {
      isPuppy = true
    }
  } else {
    if (age < 21) {
      isPuppy = true
    }
  }

  isAdult = !isPuppy

  isSenior = false
  if (isAdult) {
    if (age > 10 * 12) {
      isSenior = true
    }
    if (age >= 7 * 12 && age <= 10 * 12 && weight > 15) {
      isSenior = true
    }
  }

  // TODO: we may decide we do not need all this separate booleans.
  // We can refactor this function later, based on the actual consumption needs.
  return { idealWeight, isPuppy, isAdult, isSenior }
}

export function isTokenExpired (token) {
  return jwtDecode(token).exp < Date.now() / 1000
}

export function sleep (seconds) {
  return new Promise(resolve => setTimeout(resolve, seconds * 1000))
}

export function debounce (callback, delay = 500) {
  let timeout
  return (...args) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => callback(...args), delay)
  }
}

export async function hash (string, { key, algorithm, type = 'SHA-256' } = {}) {
  if (!string) return

  const encoder = new TextEncoder()
  const utf8 = encoder.encode(string)
  let hashBuffer

  if (algorithm && key) {
    const cryptoKeyParams = {
      format: 'raw',
      keyData: encoder.encode(key),
      algorithm: {
        name: algorithm,
        hash: type,
      },
      extractable: false,
      keyUsages: ['sign', 'verify'],
    }
    const cryptoKey = await crypto.subtle.importKey(
      cryptoKeyParams.format,
      cryptoKeyParams.keyData,
      cryptoKeyParams.algorithm,
      cryptoKeyParams.extractable,
      cryptoKeyParams.keyUsages,
    )

    hashBuffer = await crypto.subtle.sign(algorithm, cryptoKey, utf8)
  } else {
    hashBuffer = await crypto.subtle.digest(type, utf8)
  }

  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray
    .map((bytes) => bytes.toString(16).padStart(2, '0'))
    .join('')
  return hashHex
}

export function getCouponDiscountPercent (coupon) {
  if (!coupon) return 0
  // Special system coupons end with a letter.
  // 'P' applies only to subscription.
  // 'S' applies to trial, first renewal, and subscription.
  // 'E' Can only be applied within 30 min after the lead creation.
  // 'F' Can only be applied within 3 days after the lead creation.
  // 'G' Can only be applied within 12 days after the lead creation.
  // 'X Applies to one renovation only, after that it is deleted.

  const couponSubcategory = getCouponSubcategory(coupon)
  const isCouponSpecialSys = ['isSubscriptionOnly', 'isLifetime', 'isSingleTime', 'expiresInMinutes'].some(key => !!couponSubcategory?.[key])
  const isCouponReferral = coupon.includes('-')
  const isCouponRegular = !isCouponSpecialSys && !isCouponReferral

  if (isCouponReferral) return config.defaultReferralDiscountPercent
  if (isCouponSpecialSys) return Number(coupon.slice(-3, -1)) / 100
  if (isCouponRegular) return Number(coupon.slice(-2)) / 100

  return 0
}

export function getCouponSubcategory (coupon) {
  if (!coupon) return

  const couponSubcategoryKey = coupon.slice(-1)
  const couponSubcategory = config.coupons.code[couponSubcategoryKey]

  if (couponSubcategory) return couponSubcategory
  else if (coupon.includes('-')) return { isReferral: true }
}

export function areEqual (obj1, obj2) {
  if (obj1 === obj2) {
    return true
  }

  // Return false if both params are not objects (or are undefined).
  if (!obj1 || typeof obj1 !== 'object' || !obj2 || typeof obj2 !== 'object') return false

  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  if (keys1.length !== keys2.length) {
    return false
  }

  for (const key of keys1) {
    if (!keys2.includes(key) || !areEqual(obj1[key], obj2[key])) {
      return false
    }
  }

  return true
}

export function getDistributedDogMenu (currentMenu, allowedMenus, totalServings, daylyBags) {
  const newMenu = {}
  const originalTotalServings = Object.values(currentMenu).reduce((acc, qty) => acc + qty, 0)

  // Determine if we need to double or halve the servings.
  const factor = totalServings / originalTotalServings

  // Distribute new quantities.
  let integerServings = 0

  for (const [menu, qty] of Object.entries(currentMenu)) {
    if (allowedMenus.includes(menu)) {
      const newQty = Math.floor(qty * factor)
      newMenu[menu] = newQty
      integerServings += newQty
    }
  }

  // Calculate remaining servings.
  const remainingServings = totalServings - integerServings

  if (remainingServings > 0) {
    const menusWithQty = Object.values(currentMenu).filter((qty) => qty > 0)
    // If all menus are the same flavor and the user marks this flavor as an allergy, distribute menus based only on allowed flavors.
    // Otherwise, to align with user preferences, distribute the remaining bags based solely on the selected flavors.
    const menus = menusWithQty.length === 1
      ? allowedMenus
      : Object.keys(currentMenu).filter((menu) => currentMenu[menu] > 0 && allowedMenus.includes(menu))

    const incFactor = daylyBags < 1 ? 1 : daylyBags

    for (let i = 0; i < remainingServings; i += incFactor) {
      if (!newMenu[menus[i % menus.length]]) newMenu[menus[i % menus.length]] = 0
      newMenu[menus[i % menus.length]] += incFactor
    }
  }

  return newMenu
}

export function getDogAvatar (avatar, dogIdx) {
  if (!avatar) return `dodi-${dogIdx % 3 + 1}`

  return `${import.meta.env.VUE_APP_CUST_ASSETS_URL}/${avatar}`
}

// Splits a text into two parts: text and emoji.
// Returns an object with clean text without emojis and all emojis concatenated.
// Useful when you need to separate emojis from text and you don't care about the order.
export function separateEmojiFromText (text) {
  if (!text) return { text: '', emoji: '' }

  const emojis = text.match(customRegex.emoji) || []

  const emoji = emojis.join('')

  const cleanText = text.replace(customRegex.emoji, '').trim()

  return { text: cleanText, emoji }
}

// Formats text containing emojis into an array of segments for component rendering.
// Returns an array of objects with type ('text' or 'emoji') and content.
// Useful for rendering text with emoji components while preserving text order.
export function formatTextWithEmojis (text) {
  if (!text) return []

  const formattedContent = []
  let lastEmojiIndex = 0

  for (const match of text.matchAll(customRegex.emoji)) {
    const textBeforeEmoji = text.slice(lastEmojiIndex, match.index)

    if (textBeforeEmoji) {
      formattedContent.push({
        type: 'text',
        content: textBeforeEmoji,
      })
    }

    formattedContent.push({
      type: 'emoji',
      content: match[0],
    })

    lastEmojiIndex = match.index + match[0].length
  }

  const remainingText = text.slice(lastEmojiIndex)
  if (remainingText) {
    formattedContent.push({
      type: 'text',
      content: remainingText,
    })
  }

  return formattedContent
}

// Shortens strings efficiently. Accepts a single string or an array of strings as input.
export function ellipsisify (items, maxLength) {
  if (typeof items === 'string') {
    return items.length > maxLength ? `${items.slice(0, maxLength)}…` : items
  }

  if (Array.isArray(items)) {
    return items.map(item => ellipsisify(item, maxLength))
  }

  return items
}

export function isStorageAvailable (storage) {
  // This function checks if the specified storage (localStorage or sessionStorage) is available in the current environment.
  // It is based on Modernizr's approach https://github.com/Modernizr/Modernizr/blob/master/feature-detects/storage/localstorage.js

  try {
    // Directly reference the window object to avoid variable reference errors.
    const storageObj = window[storage]
    if (!storageObj) return false

    // Test if storage is accessible and writable.
    const testKey = 'isStorageAvailable'
    storageObj.setItem(testKey, '1')
    storageObj.removeItem(testKey)

    return true
  } catch (error) {
    return false
  }
}
